import {
  promotionCalendarDateKey,
  startOfNextPromotionDay,
} from "./promotionEvents";

export const PROMOTION_STATUSES = Object.freeze({
  CONTACT_PENDING: "CONTACT_PENDING",
  ADDED_TO_EVENT: "ADDED_TO_EVENT",
  INVITE_SENT: "INVITE_SENT",
  INVITE_ACCEPTED: "INVITE_ACCEPTED",
  INVITE_REJECTED: "INVITE_REJECTED",
  PROMOTION_COMPLETED: "PROMOTION_COMPLETED",
});

export const PROMOTION_STATUS_LABELS = Object.freeze({
  [PROMOTION_STATUSES.CONTACT_PENDING]: "Contact Pending",
  [PROMOTION_STATUSES.ADDED_TO_EVENT]: "Added to Event",
  [PROMOTION_STATUSES.INVITE_SENT]: "Invite Sent",
  [PROMOTION_STATUSES.INVITE_ACCEPTED]: "Invite Accepted",
  [PROMOTION_STATUSES.INVITE_REJECTED]: "Invite Rejected",
  [PROMOTION_STATUSES.PROMOTION_COMPLETED]: "Promotion Completed",
});

export const PROMOTION_STATUS_VALUES = Object.freeze(
  Object.values(PROMOTION_STATUSES)
);

export const ACTIVE_EVENT_STATUSES = Object.freeze([
  PROMOTION_STATUSES.ADDED_TO_EVENT,
  PROMOTION_STATUSES.INVITE_SENT,
  PROMOTION_STATUSES.INVITE_ACCEPTED,
]);

export const INVITE_ELIGIBLE_STATUSES = Object.freeze([
  PROMOTION_STATUSES.CONTACT_PENDING,
  PROMOTION_STATUSES.INVITE_REJECTED,
]);

export const promotionStatusLabel = (status) =>
  PROMOTION_STATUS_LABELS[status] || status;

export const PROMOTION_TRANSITION_VIA = Object.freeze({
  CREATE_EVENT: "CREATE_EVENT",
  SEND_INVITE: "SEND_INVITE",
  REMOVE_FROM_EVENT: "REMOVE_FROM_EVENT",
  MARK_AS: "MARK_AS",
  COMPLETE_EVENT: "COMPLETE_EVENT",
  MARK_COMPLETED: "MARK_COMPLETED",
});

export const TRANSITION_PHASE = Object.freeze({
  BEFORE_EVENT: "BEFORE_EVENT",
  AFTER_EVENT: "AFTER_EVENT",
  ANY: "ANY",
});

export const PROMOTION_SIDE_ACTIONS = Object.freeze({
  SEND_REMINDER: "SEND_REMINDER",
});

export const PROMOTION_SIDE_ACTION_LABELS = Object.freeze({
  [PROMOTION_SIDE_ACTIONS.SEND_REMINDER]: "Send Reminder",
});

const viaList = (...vias) => Object.freeze(vias);

const transition = (status, via, extra = {}) => {
  const phase = extra.phase || TRANSITION_PHASE.ANY;
  return Object.freeze({
    status,
    via: viaList(...(Array.isArray(via) ? via : [via])),
    requiresReason: Boolean(extra.requiresReason),
    temporary: Boolean(extra.temporary),
    phase,
    requiresEventElapsed:
      Boolean(extra.requiresEventElapsed) || phase === TRANSITION_PHASE.AFTER_EVENT,
  });
};

/**
 * Employee promotion workflow graph.
 * Linear: Contact Pending → Added to Event → Invite Sent → Accepted | Rejected
 * Before the event: Accept / Reject (and Send Reminder).
 * After the event: invited employees can only be marked Accepted or Rejected.
 * Send Reminder is hidden once the event time has passed.
 * Promotion Completed is applied when an event is completed, or directly from
 * Contact Pending on the create-event list. That direct mark cannot be undone.
 *
 * Keep in sync with quest-server/constants/promotionStatuses.js
 */
export const PROMOTION_STATUS_GRAPH = Object.freeze({
  [PROMOTION_STATUSES.CONTACT_PENDING]: Object.freeze({
    transitions: Object.freeze([
      // Assign to an existing Created event (Add to event modal), then ADDED_TO_EVENT.
      transition(
        PROMOTION_STATUSES.ADDED_TO_EVENT,
        PROMOTION_TRANSITION_VIA.CREATE_EVENT
      ),
      transition(
        PROMOTION_STATUSES.PROMOTION_COMPLETED,
        PROMOTION_TRANSITION_VIA.MARK_COMPLETED
      ),
    ]),
    sideActions: Object.freeze([]),
  }),
  [PROMOTION_STATUSES.ADDED_TO_EVENT]: Object.freeze({
    transitions: Object.freeze([
      // TEMPORARY: employee status patch only. Does not send the event.
      transition(
        PROMOTION_STATUSES.INVITE_SENT,
        PROMOTION_TRANSITION_VIA.SEND_INVITE,
        { temporary: true }
      ),
      transition(
        PROMOTION_STATUSES.CONTACT_PENDING,
        PROMOTION_TRANSITION_VIA.REMOVE_FROM_EVENT
      ),
    ]),
    sideActions: Object.freeze([]),
  }),
  [PROMOTION_STATUSES.INVITE_SENT]: Object.freeze({
    transitions: Object.freeze([
      transition(
        PROMOTION_STATUSES.INVITE_ACCEPTED,
        PROMOTION_TRANSITION_VIA.MARK_AS
      ),
      transition(
        PROMOTION_STATUSES.INVITE_REJECTED,
        PROMOTION_TRANSITION_VIA.MARK_AS,
        { requiresReason: true }
      ),
    ]),
    sideActions: Object.freeze([PROMOTION_SIDE_ACTIONS.SEND_REMINDER]),
  }),
  [PROMOTION_STATUSES.INVITE_ACCEPTED]: Object.freeze({
    transitions: Object.freeze([
      transition(
        PROMOTION_STATUSES.INVITE_REJECTED,
        PROMOTION_TRANSITION_VIA.MARK_AS,
        { requiresReason: true, phase: TRANSITION_PHASE.BEFORE_EVENT }
      ),
      transition(
        PROMOTION_STATUSES.CONTACT_PENDING,
        PROMOTION_TRANSITION_VIA.MARK_AS,
        { phase: TRANSITION_PHASE.AFTER_EVENT }
      ),
      transition(
        PROMOTION_STATUSES.PROMOTION_COMPLETED,
        PROMOTION_TRANSITION_VIA.COMPLETE_EVENT,
        { phase: TRANSITION_PHASE.AFTER_EVENT }
      ),
    ]),
    sideActions: Object.freeze([]),
  }),
  [PROMOTION_STATUSES.INVITE_REJECTED]: Object.freeze({
    transitions: Object.freeze([
      transition(
        PROMOTION_STATUSES.INVITE_ACCEPTED,
        PROMOTION_TRANSITION_VIA.MARK_AS,
        { phase: TRANSITION_PHASE.BEFORE_EVENT }
      ),
      transition(
        PROMOTION_STATUSES.CONTACT_PENDING,
        PROMOTION_TRANSITION_VIA.MARK_AS,
        { phase: TRANSITION_PHASE.AFTER_EVENT }
      ),
    ]),
    sideActions: Object.freeze([]),
  }),
  [PROMOTION_STATUSES.PROMOTION_COMPLETED]: Object.freeze({
    transitions: Object.freeze([]),
    sideActions: Object.freeze([]),
  }),
});

export const normalizePromotionStatus = (status) => {
  if (status == null || status === "") return null;
  const key = String(status).trim().toUpperCase();
  return PROMOTION_STATUS_VALUES.includes(key) ? key : null;
};

const graphNode = (status) =>
  PROMOTION_STATUS_GRAPH[normalizePromotionStatus(status)] || {
    transitions: [],
    sideActions: [],
  };

export const getPromotionTransitions = (fromStatus) =>
  graphNode(fromStatus).transitions;

export const getPromotionTransition = (fromStatus, toStatus) =>
  getPromotionTransitions(fromStatus).find(
    (item) => item.status === normalizePromotionStatus(toStatus)
  ) || null;

export const getNextPromotionStatuses = (fromStatus) => [
  ...new Set(getPromotionTransitions(fromStatus).map((item) => item.status)),
];

export const actionHasVia = (action, via) =>
  Boolean(action?.via?.includes?.(via));

export const hasPromotionEventElapsed = (
  eventDate,
  now = new Date(),
  { hasEventTime = true } = {}
) => {
  if (!eventDate) return false;
  const current = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(current.getTime())) return false;

  if (hasEventTime === false) {
    const dateKey = promotionCalendarDateKey(eventDate);
    if (!dateKey) return false;
    return current.getTime() >= startOfNextPromotionDay(dateKey).getTime();
  }

  const eventAt = eventDate instanceof Date ? eventDate : new Date(eventDate);
  if (Number.isNaN(eventAt.getTime())) return false;
  return current.getTime() >= eventAt.getTime();
};

export const canCancelPromotionInvite = (invite, now = new Date()) => {
  if (invite?.status !== "INVITE_SENT") return false;
  // Empty Invite Sent events can still be cancelled (e.g. all employees reset
  // to Contact Pending) so the event is not stuck after the event date.
  if (!(invite?.employeeCount > 0)) return true;
  return !hasPromotionEventElapsed(invite?.eventDate, now, {
    hasEventTime: invite?.hasEventTime !== false,
  });
};

/** Complete only when invites were sent, the event time has passed, and the roster is not empty. */
export const canCompletePromotionInvite = (invite, now = new Date()) => {
  if (invite?.status !== "INVITE_SENT") return false;
  if (!(invite?.employeeCount > 0)) return false;
  return hasPromotionEventElapsed(invite?.eventDate, now, {
    hasEventTime: invite?.hasEventTime !== false,
  });
};

export const promotionCompleteDisabledReason = (invite, now = new Date()) => {
  if (invite?.status !== "INVITE_SENT") return undefined;
  if (!(invite?.employeeCount > 0)) {
    return "Empty events should be cancelled";
  }
  if (
    !hasPromotionEventElapsed(invite?.eventDate, now, {
      hasEventTime: invite?.hasEventTime !== false,
    })
  ) {
    return "Available after the event time";
  }
  return undefined;
};

export const isPromotionMarkAsActionEnabled = (
  action,
  { eventDate, now, hasEventTime = true } = {}
) => {
  if (!action) return false;
  const elapsed = hasPromotionEventElapsed(eventDate, now, { hasEventTime });
  const phase =
    action.phase ||
    (action.requiresEventElapsed
      ? TRANSITION_PHASE.AFTER_EVENT
      : TRANSITION_PHASE.ANY);
  if (phase === TRANSITION_PHASE.AFTER_EVENT) return elapsed;
  if (phase === TRANSITION_PHASE.BEFORE_EVENT) return !elapsed;
  return true;
};

/** Bulk Mark As: every selected row must be in the same event phase. */
export const isPromotionMarkAsActionEnabledForRows = (
  action,
  rows = [],
  { now } = {}
) => {
  if (!action || !rows.length) return false;
  return rows.every((row) =>
    isPromotionMarkAsActionEnabled(action, {
      eventDate: row?.eventDate,
      hasEventTime: row?.hasEventTime !== false,
      now,
    })
  );
};

/** RSVP marks are hidden once the event has passed. Completion stays visible but disabled until then. */
export const isPromotionMarkAsActionVisible = (
  action,
  rows = [],
  timing = {}
) => {
  if (!action) return false;
  if (action.phase !== TRANSITION_PHASE.BEFORE_EVENT) return true;
  return isPromotionMarkAsActionEnabledForRows(action, rows, timing);
};

export const promotionMarkAsDisabledReason = (
  action,
  rows = [],
  timing = {}
) => {
  if (!action || isPromotionMarkAsActionEnabledForRows(action, rows, timing)) {
    return undefined;
  }
  if (
    action.phase === TRANSITION_PHASE.AFTER_EVENT ||
    action.requiresEventElapsed
  ) {
    return "Available after the event time";
  }
  if (action.phase === TRANSITION_PHASE.BEFORE_EVENT) {
    return "This status change is only available before the event time";
  }
  return undefined;
};

export const getMarkAsTransitions = (fromStatus) =>
  getPromotionTransitions(fromStatus).filter((item) =>
    item.via.includes(PROMOTION_TRANSITION_VIA.MARK_AS)
  );

export const getMarkAsStatuses = (fromStatus) =>
  getMarkAsTransitions(fromStatus).map((item) => item.status);

export const canTransitionPromotionStatus = (fromStatus, toStatus, via) => {
  const next = normalizePromotionStatus(toStatus) || toStatus;
  return getPromotionTransitions(fromStatus).some(
    (item) => item.status === next && (!via || item.via.includes(via))
  );
};

export const canMarkAsPromotionStatus = (fromStatus, toStatus) =>
  canTransitionPromotionStatus(
    fromStatus,
    toStatus,
    PROMOTION_TRANSITION_VIA.MARK_AS
  );

export const promotionTransitionRequiresReason = (fromStatus, toStatus) => {
  const next = normalizePromotionStatus(toStatus) || toStatus;
  return getPromotionTransitions(fromStatus).some(
    (item) => item.status === next && item.requiresReason
  );
};

export const markAsActionLabel = (toStatus) => {
  const label = promotionStatusLabel(toStatus);
  if (toStatus === PROMOTION_STATUSES.CONTACT_PENDING) {
    return `Reset to ${label}`;
  }
  return label;
};

const toPromotionAction = (item) =>
  Object.freeze({
    key: item.status,
    label: markAsActionLabel(item.status),
    type: "status",
    via: item.via,
    requiresReason: item.requiresReason,
    temporary: item.temporary,
    phase: item.phase,
    requiresEventElapsed: item.requiresEventElapsed,
  });

export const getPromotionMarkAsActions = (fromStatus) =>
  getPromotionTransitions(fromStatus)
    .filter(
      (item) =>
        item.via.includes(PROMOTION_TRANSITION_VIA.MARK_AS) ||
        item.via.includes(PROMOTION_TRANSITION_VIA.CREATE_EVENT)
    )
    .map(toPromotionAction);

/** Gmail-style split: primary action vs extra statuses in the caret menu. */
export const splitMarkAsActions = (fromStatus) => {
  const actions = getPromotionMarkAsActions(fromStatus);
  return {
    primary: actions[0] || null,
    extras: actions.slice(1),
  };
};

export const getPromotionSideActions = (
  fromStatus,
  { eventDate, now, hasEventTime = true } = {}
) => {
  const elapsed = hasPromotionEventElapsed(eventDate, now, { hasEventTime });
  return (graphNode(fromStatus).sideActions || [])
    .filter((key) => {
      if (key === PROMOTION_SIDE_ACTIONS.SEND_REMINDER) return !elapsed;
      return true;
    })
    .map((key) =>
      Object.freeze({
        key,
        label: PROMOTION_SIDE_ACTION_LABELS[key] || key,
        type: "side",
      })
    );
};

export const getPromotionRowActions = (fromStatus, timing = {}) => [
  ...getPromotionMarkAsActions(fromStatus).filter((action) =>
    isPromotionMarkAsActionEnabled(action, timing)
  ),
  ...getPromotionSideActions(fromStatus, timing),
];
